import { expect } from "./expect";
import {getRootDomain} from  './utils/cookieUtils'

export type IFrameOptions = {
  ensureNotExists: boolean,
  attributes: any
}

export interface IBrowserStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export interface IBrowserAccessor {
  get sessionStorage(): IBrowserStorage;
  get localStorage(): IBrowserStorage;
  get cookieStorage(): IBrowserStorage;
  getPageUrl(): string;
  getPageTitle(): string;
  getScreenResolution(): string;
  getUserAgent(): string;
  getReferrer(): string;
  createTrackingPixel(url: string): void;
  createIFrame(url: string, options: IFrameOptions, onCreated?: () => void, onError?: () => void): void;
}

export class DefaultBrowserAccessor implements IBrowserAccessor {
  private readonly window: Window;
  private readonly sessionStorageAsyncWrapper: IBrowserStorage;
  private readonly localStorageAsyncWrapper: IBrowserStorage;
  private readonly cookieStorageAsyncWrapper: IBrowserStorage;

  constructor(window: Window) {
    this.window = expect(window).toBeDefined('The window object is required to construct DefaultBrowserAccessor.');
    this.sessionStorageAsyncWrapper = {
      getItem: (key: string) => Promise.resolve(this.window.sessionStorage.getItem(key)),
      setItem: (key: string, value: string) => Promise.resolve(this.window.sessionStorage.setItem(key, value)),
      removeItem: (key: string) => Promise.resolve(this.window.sessionStorage.removeItem(key))
    };
    this.localStorageAsyncWrapper = {
      getItem: (key: string) => Promise.resolve(this.window.localStorage.getItem(key)),
      setItem: (key: string, value: string) => Promise.resolve(this.window.localStorage.setItem(key, value)),
      removeItem: (key: string) => Promise.resolve(this.window.localStorage.removeItem(key))
    };
    this.cookieStorageAsyncWrapper = {
      getItem: (key: string) => {
        const name = key + "=";
        const cookies = this.window.document.cookie.split(';').map(cookie => cookie.trim());
        const cookie = cookies.find(c => c.startsWith(name));
        return Promise.resolve(cookie ? cookie.substring(name.length) : null);
      },
      setItem: (key: string, value: string) => {
        this.window.document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${this.window.document.domain}; SameSite=Lax`; // remove the duplicate cooki which was saved outside the sdk
        const domain = getRootDomain(this.window.location.hostname);
        const expires = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000).toUTCString(); // 2 years
        this.window.document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires}; path=/; domain=${domain}; SameSite=Lax`;
        return Promise.resolve();
      },
      removeItem: (key: string) => {
        const domain = getRootDomain(this.window.location.hostname);
        this.window.document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${domain}; SameSite=Lax`;
        return Promise.resolve();
      }
    };

  }

  public getPageUrl(): string {
    return this.window.location.href;
  }
  public getPageTitle(): string {
    return this.window.document.title;
  }
  public getScreenResolution(): string {
    return `${this.window.screen.width}x${this.window.screen.height}`
  }
  public getUserAgent(): string {
    return this.window.navigator.userAgent;
  }
  public getReferrer(): string {
    return this.window.document.referrer;
  }
  public get sessionStorage(): IBrowserStorage {
    return this.sessionStorageAsyncWrapper;
  }
  public get localStorage(): IBrowserStorage {
    return this.localStorageAsyncWrapper;
  }
  public get cookieStorage(): IBrowserStorage {
    return this.cookieStorageAsyncWrapper;
  }
  createTrackingPixel(url: string): void {
    // the code below is copied from globale.merchant.client.js:
    const img = this.window.document.createElement("img");
    img.width = 1;
    img.height = 1;
    img.src = url;
  }
  createIFrame(url: string, options: IFrameOptions, onCreated?: () => void, onError?: () => void): void {
    let iframe = this.window.document.getElementById(options.attributes.id);
    if (!iframe) {
        iframe = this.window.document.createElement("iframe");
        iframe.setAttribute('id', options.attributes.id);
        iframe.setAttribute('title', options.attributes.title);
        iframe.setAttribute('width', options.attributes.width);
        iframe.setAttribute('height', options.attributes.height);
        iframe.style.display = "none";
        this.window.document.getElementsByTagName("body")[0].appendChild(iframe);
    }
    iframe.setAttribute("src", url);
  }
}

export class ShopifyBrowserAccessor implements IBrowserAccessor {
  private readonly _sessionStorage: IBrowserStorage;
  private readonly _localStorage: IBrowserStorage;
  private readonly _cookieStorage: IBrowserStorage;

  constructor(sessionStorage: IBrowserStorage, localStorage: IBrowserStorage, cookieStorage: IBrowserStorage) {
    this._sessionStorage = expect(sessionStorage).toBeDefined();
    this._localStorage = expect(localStorage).toBeDefined();
    this._cookieStorage = expect(cookieStorage).toBeDefined();
  }
  public getPageUrl(): string {
    // CORE-122326 - exception suppressed, due to issue found during POC for Shopify Checkout.
    //throw new NotSupportedError('getPageUrl() of ShopifyBrowserAccessor');
    return undefined;
  }
  public getPageTitle(): string {
    // CORE-122326 - exception suppressed, due to issue found during POC for Shopify Checkout.
    //throw new NotSupportedError('getPageTitle() of ShopifyBrowserAccessor');
    return undefined;
  }
  public getScreenResolution(): string {
    // CORE-122326 - exception suppressed, due to issue found during POC for Shopify Checkout.
    //throw new NotSupportedError('getScreenResolution() of ShopifyBrowserAccessor');
    return undefined;
  }
  public getUserAgent(): string {
    // CORE-122326 - exception suppressed, due to issue found during POC for Shopify Checkout.
    //throw new NotSupportedError('getUserAgent() of ShopifyBrowserAccessor');
    return undefined;
  }
  public getReferrer(): string {
    // CORE-122326 - exception suppressed, due to issue found during POC for Shopify Checkout.
    //throw new NotSupportedError('getReferrer() of ShopifyBrowserAccessor');
    return undefined;
  }
  public get sessionStorage(): IBrowserStorage {
    return this._sessionStorage;
  }
  public get localStorage(): IBrowserStorage {
    return this._localStorage;
  }

  get cookieStorage(): IBrowserStorage {
    return this._cookieStorage;
  }

  createTrackingPixel(url: string): void {
    // CORE-122326 - exception suppressed, due to issue found during POC for Shopify Checkout.
    //throw new NotSupportedError('createTrackingPixel() of ShopifyBrowserAccessor');
    return undefined;
  }
  createIFrame(url: string, options: IFrameOptions, onCreated?: () => void, onError?: () => void): void {
    // CORE-122326 - exception suppressed, due to issue found during POC for Shopify Checkout.
    //throw new NotSupportedError('createIFrame() of ShopifyBrowserAccessor');
    return undefined;
  }
}

// todo: custom browser accessor with only limitted functionality provided from the outside of init method
